import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { TreeStructure, ArrowLeft, Database, GitBranch, ListChecks, Scroll } from "@phosphor-icons/react";
import api from "../api";

const Chip = ({ children }) => (
  <span className="rounded-full border border-ff-brass/25 bg-ff-bg px-3 py-1 text-xs text-ff-parchment/90">{children}</span>
);

const Section = ({ icon, title, children, testid }) => (
  <div className="ff-panel rounded-md p-5" data-testid={testid}>
    <h2 className="flex items-center gap-2 text-lg font-semibold text-ff-gold">{icon} {title}</h2>
    <div className="mt-3">{children}</div>
  </div>
);

export default function CommissionedArchitecture() {
  const [data, setData] = useState(null);
  const [badges, setBadges] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get("/admin/commissioned/architecture").then((r) => setData(r.data)).catch(() => setErr(true));
    api.get("/admin/commissioned/badges").then((r) => setBadges(r.data)).catch(() => {});
  }, []);

  if (err) return <div className="ff-panel rounded-md p-8 text-center text-ff-muted" data-testid="ce-arch-error">Access restricted.</div>;
  if (!data) return <div className="ff-panel rounded-md p-8 text-center text-ff-muted">Loading architecture…</div>;

  const s = data.status || {};

  return (
    <div className="space-y-6" data-testid="ce-arch-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TreeStructure size={26} weight="duotone" className="text-ff-gold" />
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">Commissioned Expeditions</h1>
        </div>
        <Link to="/admin" data-testid="ce-arch-back" className="flex items-center gap-1 rounded-sm border border-ff-brass/30 px-3 py-1.5 text-sm text-ff-muted hover:border-ff-gold hover:text-ff-gold">
          <ArrowLeft size={16} /> Admin
        </Link>
      </div>
      <p className="text-sm text-ff-muted">{data.title} — documentation only. No controls, funding, or financial records exist here.</p>

      <div className="ff-panel rounded-md p-5" data-testid="ce-arch-status">
        <h2 className="text-lg font-semibold text-ff-gold">Program status</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
          <div>Feature flag: <span className={`font-semibold ${s.enabled ? "text-red-400" : "text-ff-parchment"}`}>{String(s.enabled)}</span></div>
          <div>Phase: <span className="text-ff-parchment">{s.phase}</span></div>
          <div>Financial functionality: <span className="font-semibold text-ff-parchment">{String(s.financial_functionality_active)}</span></div>
          <div>Earnings wallet: <span className="font-semibold text-ff-parchment">{String(s.earnings_wallet_exists)}</span></div>
          <div>Withdrawals: <span className="font-semibold text-ff-parchment">{String(s.withdrawals_available)}</span></div>
          <div>Payment providers: <span className="font-semibold text-ff-parchment">{String(s.payment_providers_connected)}</span></div>
          <div>Crypto: <span className="font-semibold text-ff-parchment">{String(s.crypto_supported)}</span></div>
          <div>Affects rankings/Prestige: <span className="font-semibold text-ff-parchment">{String(s.affects_rankings_or_prestige)}</span></div>
          <div>Minimum age: <span className="font-semibold text-ff-parchment">{s.min_age}+</span></div>
        </div>
      </div>

      <Section icon={<GitBranch size={20} weight="duotone" />} title="Commissioned formats" testid="ce-arch-formats">
        <div className="flex flex-wrap gap-2">{(data.formats || []).map((f) => <Chip key={f}>{f}</Chip>)}</div>
      </Section>

      <div className="grid gap-6 md:grid-cols-2">
        <Section icon={<ListChecks size={20} weight="duotone" />} title="Campaign states" testid="ce-arch-campaign-states">
          <div className="flex flex-wrap gap-2">{(data.campaign_states || []).map((x) => <Chip key={x}>{x}</Chip>)}</div>
        </Section>
        <Section icon={<ListChecks size={20} weight="duotone" />} title="Player submission states" testid="ce-arch-submission-states">
          <div className="flex flex-wrap gap-2">{(data.submission_states || []).map((x) => <Chip key={x}>{x}</Chip>)}</div>
        </Section>
      </div>

      <Section icon={<Scroll size={20} weight="duotone" />} title="Required player disclosures (pre-consent)" testid="ce-arch-disclosures">
        <div className="flex flex-wrap gap-2">{(data.required_player_disclosures || []).map((x) => <Chip key={x}>{x}</Chip>)}</div>
      </Section>

      <div className="grid gap-6 md:grid-cols-2">
        <Section icon={<Scroll size={20} weight="duotone" />} title="Creator ownership choices" testid="ce-arch-ownership">
          <ul className="space-y-1 text-sm text-ff-parchment/90">{(data.ownership_choices || []).map((x) => <li key={x}>◆ {x}</li>)}</ul>
        </Section>
        <Section icon={<Scroll size={20} weight="duotone" />} title="Earnings visibility options" testid="ce-arch-visibility">
          <div className="flex flex-wrap gap-2">{(data.earnings_visibility_options || []).map((x) => <Chip key={x}>{x}</Chip>)}</div>
        </Section>
      </div>

      <Section icon={<Database size={20} weight="duotone" />} title="Future data models (not created)" testid="ce-arch-models">
        <div className="flex flex-wrap gap-2">{(data.future_data_models || []).map((x) => <Chip key={x}>{x}</Chip>)}</div>
      </Section>

      <div className="space-y-3">
        {["reward_allocation_note", "payment_architecture_note", "integrity_note", "current_scope"].map((k) => (
          <div key={k} className="rounded-md border border-ff-brass/25 bg-ff-bg2 p-4 text-sm text-ff-muted" data-testid={`ce-arch-${k}`}>
            {data[k]}
          </div>
        ))}
      </div>

      {badges && (
        <Section icon={<Scroll size={20} weight="duotone" />} title="Sponsor Expedition Badge System" testid="ce-arch-badges">
          <div className="mb-3 grid gap-2 text-sm sm:grid-cols-3">
            <div>Badges enabled: <span className="font-semibold text-ff-parchment">{String(badges.status.enabled)}</span></div>
            <div>Real badges issued: <span className="font-semibold text-ff-parchment">{String(badges.status.real_badges_issued)}</span></div>
            <div>Affects standing: <span className="font-semibold text-ff-parchment">{String(badges.status.affects_gold_prestige_rank_mastery_or_leaderboard)}</span></div>
          </div>
          <div className="mb-2 text-xs uppercase tracking-widest text-ff-brass">Categories</div>
          <div className="flex flex-wrap gap-2">{(badges.categories || []).map((c) => <Chip key={c}>{c}</Chip>)}</div>
          <div className="mb-2 mt-3 text-xs uppercase tracking-widest text-ff-brass">Tiers</div>
          <div className="flex flex-wrap gap-2">{(badges.tiers || []).map((c) => <Chip key={c}>{c}</Chip>)}</div>
          <div className="mb-2 mt-3 text-xs uppercase tracking-widest text-ff-brass">Placeholder data models</div>
          <div className="flex flex-wrap gap-2"><Chip>sponsor_badges</Chip><Chip>player_sponsor_badges</Chip></div>
          <p className="mt-3 text-xs text-ff-muted">{badges.current_scope}</p>
        </Section>
      )}
    </div>
  );
}
